import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NgForm } from "@angular/forms";
import { Router } from "@angular/router";
import { HttpErrorResponse } from '@angular/common/http';

import { TranslateService } from "@ngx-translate/core";
import { UsersService } from "@app/services/net-remit/users.service";
import { SharedDataService } from "@app/services/net-remit/shared-data.service";
import { AuthenticationService } from "@app/core/authentication/authentication.service";
import { SharedService } from "@app/services/net-remit/shared-functions.service";
import { MessageService } from "primeng/api";

import { LoginContext } from '@app/shared';
import { ComboKeysView } from "@app/shared/models/ComboKeysView";
import { routesConstants } from "@app/shared/models/LinksView";

import { default as constantsFile } from '@app/shared/constants.json';
import { environment } from "@env/environment";
import { finalize , takeWhile } from "rxjs/operators";

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false
})
export class LoginComponent implements OnDestroy, OnChanges{

  alive: boolean = true;
  constants: any = constantsFile.constants;
  firstLoginSuccess: boolean;
  isLoading: boolean;
  hideForgot : boolean;
  languagesList = new Array<ComboKeysView>();
  loginData: LoginContext = new Object() as LoginContext;
  loginLocked: boolean;
  patternMfaCode: string = this.constants.pattern.mfaCode;
  patternUniqueEmail: string = this.constants.pattern.uniqueEmail;
  showMFAInfo: boolean;

  @Input() language: string;
  @Input() languages: string[];
  @Input() returnUrl: string;
  @Output() toggleShowMFAInformationMethod = new EventEmitter<boolean>();
  @Output() setLanguageMethod = new EventEmitter<string>();
  @ViewChild('loginForm') public loginForm: NgForm;

  constructor(
    private userService: UsersService,
    private translateService: TranslateService,
    private sharedDataService: SharedDataService,
    private sharedService: SharedService,
    private router: Router,
    private authenticationService: AuthenticationService,
    private messageService: MessageService
  ) {
    const lang = localStorage.getItem("language");
    this.translateService.use(!lang ? environment.defaultLanguage : lang);
    this.translateService.langs = environment.supportedLanguages;
  }

  ngOnChanges(changes: SimpleChanges) {
    if(changes && changes.languages){
      const updatedLanguages = changes.languages.currentValue;
      this.languagesList = updatedLanguages.map((language: string) => {
        return new ComboKeysView(language, language);
      });
    }
  }

  public login() {
    this.loginData.language = this.translateService.currentLang;
    this.isLoading = true;
    window.localStorage.removeItem(this.constants.keys.CRMFilters);
    window.sessionStorage.removeItem(this.constants.keys.sortFieldsKey);
    window.sessionStorage.removeItem(this.constants.keys.hideDeactivatedKey);
    window.sessionStorage.removeItem(this.constants.keys.filterDateKey);

    this.userService.getUser(this.loginData)
      .pipe(
        finalize(() => {
          this.loginForm?.form.markAsPristine();
          this.isLoading = false;
        }),
        takeWhile(() => this.alive)
      )
      .subscribe(
        { next: (response) =>
          {
            this.getResponseLogin(response);        
          }, 
          error: (error: HttpErrorResponse) =>
          {
            this.checkTypeError(error);
          }
        }
      );
  }

  public getResponseLogin(response: any)
  {
    const tokenInfo = response.body;

    // Validación para tokenInfo null
    if (!tokenInfo) {
      this.messageService.add({
        key: 'toast-login',
        sticky: true,
        severity: 'error',
        summary: this.translateService.instant('Modal.Error'),
        detail: this.translateService.instant('Login.Errors.Login Error')
      });
      return;
    }    

    if (response.status === 202) {
      this.firstLoginSuccess = true;
      this.showMFAInfo = true;
      if (tokenInfo.isMfaReset) {
        this.toggleShowMFAInformation(true);
        this.userService.accessMfa(this.loginData.username)
          .pipe(finalize(() => {}), takeWhile(() => this.alive))
          .subscribe({ next: () => {
            this.isLoading = false;
            }, error: (error: HttpErrorResponse) => {
              this.checkTypeError(error);
            }});
      }
      this.sharedService.sendData({
        img: 'data:image/png;base64,' + response.body.qr,
        code: response.body.code
      });
    } else {
      this.authenticationService.login(tokenInfo);
      if (this.authenticationService.credentials.roleName.includes(this.constants.roleNames.admin) || this.authenticationService.credentials.isWelcomeMessageRead) {
        if (this.authenticationService.credentials.isOnlyPremium) {
          this.returnUrl = routesConstants.analytics;
        }
      } else {
        this.returnUrl = routesConstants.welcome;
      }
      window.sessionStorage.removeItem(constantsFile.constants.commFilter.sessionStorageCrm);
      this.router.navigateByUrl(this.returnUrl).then(r => {});
    }    
  }

  public forgotPassword() {
    if (this.isLoading) { return } // Ya se ha pulsado antes y no ha terminado
    this.isLoading = true;
    if (!this.loginData.username) { // Si no hay user name y si pulsa "Olvidé mi contraseña"
      this.messageService.add({
        key: 'toast-login',
        sticky: true,
        severity:'info',
        summary: this.translateService.instant('Modal.Info'),
        detail: this.translateService.instant('Login.Errors.Enter Username')
      });
      return;
    }
    this.userService.forgotPassword(this.loginData)
      .pipe(finalize(() => { this.isLoading = false }), takeWhile(() => this.alive))
      .subscribe({ next: () => {
        this.hideForgot = true;
          this.messageService.add({
            key: 'toast-login',
            sticky: true,
            severity:'info',
            summary: this.translateService.instant('Modal.Info'),
            detail: this.translateService.instant('Login.Recovery Email Sent')
          });
      }, error: (error) => {
          this.messageService.add({
            key: 'toast-login',
            sticky: true,
            severity:'info',
            summary: this.translateService.instant('Modal.Info'),
            detail: this.translateService.instant(error.message)
          });
      }});
  }

  private checkTypeError(error: HttpErrorResponse){
    switch (error.status){
      case 400:
        if (typeof error.error === 'object') {
          this.messageService.add({
            key: 'toast-login',
            sticky: true,
            severity:'error',
            summary: this.translateService.instant('Modal.Error'),
            detail: this.translateService.instant(error.error.description, { attemptsRemaining: error.error.attemptsRemaining} )
          });
        } else if (typeof error.error === 'string') {
          this.messageService.add({
            key: 'toast-login',
            sticky: true,
            severity:'error',
            summary: this.translateService.instant('Modal.Error'),
            detail: this.translateService.instant(error.error)
          });
        }
        break;
      case 429:
        this.messageService.add({
          key: 'toast-login',
          sticky: true,
          severity:'error',
          summary: this.translateService.instant('Modal.Error'),
          detail: this.translateService.instant(error.error.description, { lockEndMinutes: Math.ceil(error.error.lockEndMinutes * 100) / 100 } )
        });
        break;
      default:
        this.messageService.add({
          key: 'toast-login',
          sticky: true,
          severity:'error',
          summary: this.translateService.instant('Modal.Error'),
          detail: this.translateService.instant('Login.Errors.Login Error')
        });
        break;
    }
  }

  public toggleShowMFAInformation(value: boolean){
    this.toggleShowMFAInformationMethod.emit(value);
  }

  public setLanguage(language) {
    if(language){
      this.setLanguageMethod.emit(language);
    }
  }

  public getContactUsEmails(){
    this.sharedDataService.getContactUsEmails();
  }

  ngOnDestroy() {
    this.alive = false;
  }
}
